<template>
  <teleport to="body">
    <transition name="modal-visible">
      <div
        v-if="isVisible"
        class="modal-overlay"
        @click="onCloseFromOverlay"
        :class="{
          'modal-overlay--modal-right': props.align == TirPmModalAlignEnum.Right,
          'modal-hide': closeModal && props.align == TirPmModalAlignEnum.Right,
        }"
      >
        <div class="modal" ref="$content" :class="classes">
          <div class="modal__header">
            <div class="modal__wrap-info-header">
              <div
                class="modal__back-button"
                v-if="props.isWithBack"
                @click="() => (props.isWithStep ? toBackStep() : onClose())"
              >
                <ArrowLeftIcon /> Назад
              </div>
              <div class="modal__title" v-if="props.title || isTitleSlot">
                <slot name="title">
                  {{ props.title }}
                </slot>
              </div>
            </div>
            <div class="modal__close-button" v-if="props.isWithCloseButton" @click="onClose">
              <XMarkIcon />
            </div>
          </div>
          <TirPmScrollbar class="modal__content-scroll" v-if="isContent">
            <div class="modal__content">
              <slot name="content"></slot></div
          ></TirPmScrollbar>
          <div class="modal__footer" v-if="isFooter">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts" setup>
import { computed, PropType, ref, useSlots, onUnmounted, defineEmits } from "vue";
import { ArrowLeftIcon, XMarkIcon } from "tir-style-system/icons/solid";
import { TirPmScrollbar } from "tir-pm-scrollbar";
import { TirPmModalAlignEnum } from "./enums";

/**Параметры */
const props = defineProps({
  /**Видимость */
  isVisible: { type: Boolean as PropType<boolean>, default: false },
  /** Показывать ли кнопку назад */
  isWithBack: { type: Boolean as PropType<boolean>, default: true },
  /** Закрывать ли по клику */
  isCloseOverlay: { type: Boolean as PropType<boolean>, default: false },
  /** Положение */
  align: {
    type: String as PropType<TirPmModalAlignEnum>,
    default: TirPmModalAlignEnum.Center,
  },
  /**Заголовок */
  title: { type: String as PropType<string>, default: "" },
  /**Делает кнопку "назад" считалкой стпов внутри конктентной части */
  isWithStep: { type: Boolean as PropType<boolean>, default: false },
  /** Текущий номер шага */
  numberStep: { type: Number as PropType<number>, default: 0 },
  /** Показывать ли кнопку закрытия */
  isWithCloseButton: { type: Boolean as PropType<boolean>, default: true },
  /**Классы для modal элемента */
  classes: { type: String, default: "" },
});
/**События */
const emit = defineEmits<{
  (eventName: "close"): void;
  (eventName: "open"): void;
  (eventName: "backStep"): void;
}>();

const closeModal = ref(false);
/**DOM элемент блока с классом modal */
const $content = ref<HTMLElement>();
/**При закрытии */
const onClose = () => {
  emit("close");
};
/**При закрытии на оверлее */
const onCloseFromOverlay = (e: MouseEvent | TouchEvent) => {
  // closeModal.value = true;

  if (props.isCloseOverlay) {
    const $target = e.target as HTMLElement;
    if (
      !$content.value ||
      !$target ||
      $target == $content.value ||
      $content.value.contains($target)
    ) {
      return;
    } else {
      closeModal.value = true;
    }
    setTimeout(() => {
      emit("close");
      closeModal.value = false;
    }, 0.2);
  }
};

/** К предыдущему шагу */
const toBackStep = () => {
  if (props.numberStep !== 0) {
    emit("backStep");
  } else {
    emit("close");
  }
};
/**Видимость */
const visible = computed(() => props.isVisible);
/**Слоты */
const slots = useSlots();
/**Есть ли дефолный футер */
const isFooter = computed(() => slots && slots.footer && slots.footer());

const isContent = computed(() => slots && slots.content && slots.content());
/**Если слот под заголовок */
const isTitleSlot = computed(() => slots && slots.title && slots.title());

onUnmounted(() => {
  if (visible.value) {
    emit("close");
    document.body.style.removeProperty("overflow");
  }
});
</script>

<style lang="scss">
@import "./style.scss";
</style>
